import { type CSSProperties } from "react";

import { type Group, type ProfileDerivedVariant, type Project, type Variant, ZygosityEnum } from "../../graphql.js";
import { highlightMutantGenome, type IGenericVariant, type IMutantGenome } from "../../redux/modules/genome-browser.js";
import { type ReduxDispatch } from "../../redux/redux-utils.js";
import { type CallTooltip } from "../../ui-modules/genome-viewer/features-track/FeatureTrack.js";
import {
  type IVariantProfileTooltipData,
  type IVariantSymbolTooltipData,
} from "../../ui-modules/genome-viewer/features-track/VariantTooltips.js";
import { getColorScheme, selectedColorScheme } from "../genome-browser-color.js";
import { TRACK_TOOLTIP_TYPE } from "./shape-feature.js";
import { type IPileupParams, type IPileupVariant } from "./track-pileup-factory.js";
import {
  type GenomeViewerShape,
  type GenomeViewerShapeInteractivity,
  type getMutationShapeRenderer,
} from "./track-shapes-konva-renderers.js";

export interface ICreateVariantShapeParams {
  pileupVariant: IPileupVariant;
  pileupParams: IPileupParams;
  selectedIds: number[];
  renderMutationShape: ReturnType<typeof getMutationShapeRenderer>["renderMutationShape"];
  renderConnector: ReturnType<typeof getMutationShapeRenderer>["renderMutationConnector"];
  renderProfileBox: ReturnType<typeof getMutationShapeRenderer>["renderProfileBox"];
  callTooltip: CallTooltip;
  groups: Map<Project, Group[]>;
  mutantGenomes: IMutantGenome[];
  dispatch: ReduxDispatch;
}

/**
 * A visualization component that draws the shape for a variant in the variants track.
 *
 * The component is expected to be used within an existing SVG context and does NOT create its own SVG element. The
 * main input data is pileup variant whose view coordinates and a width have already been computed elsewhere. The
 * VariantShape renders a mutation-type-specific shape:
 * - Point-Mutation: diamond shape
 * - Deletion: top-pointing triangle
 * - Insertion: down-pointing triangle
 * - InDel: overlay of one top- and one down-pointing triangle
 *
 * Overview of shape dimensions:
 *
 * <g>
 * | (x,y) ¦             (midpoint)                    |
 * | <-------------------- width --------------------> |
 * | <-- midpointOffset --> ¦                          |
 * |               | <-- symbolWidth --> ¦             |
 * </g>
 *
 * created on 02/01/2017
 */
const createVariantShape = (params: ICreateVariantShapeParams): GenomeViewerShapeInteractivity => {
  const {
    pileupVariant,
    pileupParams,
    selectedIds,
    renderMutationShape,
    renderConnector,
    renderProfileBox,
    callTooltip,
    mutantGenomes,
    groups,
    dispatch,
  } = params;

  // calculate start vertical position of shape
  const shapeY = pileupVariant.lane * (pileupParams.featureHeight + pileupParams.lanePadding);
  const firstVariant = pileupVariant.variants[0];

  const variantCount: number = pileupVariant.variants.length;
  const {
    symbolElementStyle,
    rangeElementStyle,
    connectorElementStyles,
    profileElementStyles,
    naElementStyle,
    heterocygousElementStyle,
    noCallElementStyle,
  } = generateVariantStyles(firstVariant.variantType, pileupVariant.variants, selectedIds);

  // draw connectors first
  if (firstVariant.isReference && mutantGenomes?.length) {
    pileupVariant.variants.map((variant, i) => {
      // if the profile midpoint is outside the svg viewport, we do not display anything (e.g. long variant with visible range element but not visible symbol element)
      const profileMidpoint = pileupVariant.profileMidpoints?.[i];
      if (profileMidpoint == undefined || profileMidpoint < 0) return null;

      const connectorStyle = connectorElementStyles[variant.id];
      renderConnector({
        pileupVariant,
        y: shapeY,
        profileMidpoint,
        connectorStyle: connectorStyle,
      });

      const isVcfVariant = !("mutId" in variant); // this flag triggers special behavior for vcf variants
      const profileStyle = profileElementStyles[variant.id];

      mutantGenomes.map((mutantGenome, mgIndex) => {
        let mutantZygosity: ZygosityEnum | null;
        let profileChar: string;
        let noCall = false; // default false because classic variants have no noCall support

        if (isVcfVariant) {
          // for VCF variants
          const profileVariant = variant.profileVariant?.find(
            (profileVariant) => profileVariant?.genomeId === mutantGenome.id,
          );
          mutantZygosity = profileVariant?.heterozygous ? ZygosityEnum.HeteroZygous : ZygosityEnum.HomoZygous;
          noCall = profileVariant?.noCall ?? false;
          profileChar = profileVariant?.present ? "1" : "0";
        } else {
          // for classic variants
          const profile: ProfileDerivedVariant = variant.profile?.find(
            (profileVariant) => profileVariant?.genomeId === mutantGenome.id,
          );
          mutantZygosity = profile?.zygosity;
          profileChar = profile?.symbol;
        }

        const profileBoxStyle = noCall
          ? noCallElementStyle
          : profileChar === "1"
            ? mutantZygosity === ZygosityEnum.HeteroZygous
              ? heterocygousElementStyle
              : profileStyle
            : profileChar === "-"
              ? naElementStyle
              : connectorStyle;

        const profileBoxShape: GenomeViewerShape = renderProfileBox({
          pileupVariant,
          profileMidpoint,
          symbolY: shapeY,
          profileStyle: profileBoxStyle,
          boxIndex: mgIndex,
        });
        const shapeInteractivity: GenomeViewerShapeInteractivity = profileBoxShape.interactive();

        callTooltip(shapeInteractivity.interactiveShape, shapeInteractivity.tooltipTargetShape, {
          uid: `${variant.id}-${mutantGenome.id}`,
          title: mutantGenome.name,
          type: TRACK_TOOLTIP_TYPE.VARIANT_PROFILE,
          content: {
            variant,
            mutantGenome,
          } as IVariantProfileTooltipData,
        });

        shapeInteractivity.interactiveShape.on("click", () => {
          dispatch(highlightMutantGenome(Number(mutantGenome.id)));
        });
      });
    });
  }

  const symbolShape: GenomeViewerShape = renderMutationShape({
    pileupVariant: pileupVariant,
    y: shapeY,
    height: pileupParams.featureHeight,
    count: variantCount,
    showRegion:
      (firstVariant.isReference && firstVariant.refAlleleLength > 0) ||
      (!firstVariant.isReference && firstVariant.altAlleleLength > 0),
    symbolElementStyle,
    rangeElementStyle,
  });

  if (groups) {
    symbolShape.highlight(...[...groups.keys()].map((project) => project.color));
  }

  const shapeInteractivity: GenomeViewerShapeInteractivity = symbolShape.interactive();

  callTooltip(shapeInteractivity.interactiveShape, shapeInteractivity.tooltipTargetShape, {
    uid: `${firstVariant.id}-group`,
    title: variantCount === 1 ? firstVariant.name : `${variantCount} variants`,
    type: TRACK_TOOLTIP_TYPE.VARIANT,
    content: {
      pileupVariant,
      groups,
    } as IVariantSymbolTooltipData,
  });

  return shapeInteractivity;
};

export interface IVariantStyles {
  symbolElementStyle: CSSProperties;
  rangeElementStyle: CSSProperties;
  connectorElementStyles: Record<number, CSSProperties>;
  profileElementStyles: Record<number, CSSProperties>;
  naElementStyle: CSSProperties;
  heterocygousElementStyle: CSSProperties;
  noCallElementStyle: CSSProperties;
}

export const generateVariantStyles = (
  variantType: string,
  pileupVariants: IGenericVariant[] = [],
  selected: number[] = [],
): IVariantStyles => {
  const variantColorKey = "variant." + variantType.toLocaleLowerCase();
  const colorScheme = getColorScheme(variantColorKey).colorScheme;
  if (!colorScheme) {
    throw new Error("Cannot get color scheme for '" + variantColorKey + "'");
  }
  const rangeElementStyle: any = {};
  const symbolElementStyle: any = {};
  const connectorElementStyles: Record<number, any> = {};
  const profileElementStyles: Record<number, any> = {};
  const naElementStyle: any = {};
  const heterocygousElementStyle: any = {};
  const noCallElementStyle: any = {};

  // default coloring
  rangeElementStyle.fill = colorScheme.light;
  symbolElementStyle.fill = colorScheme.medium;
  symbolElementStyle.stroke = colorScheme.dark;
  naElementStyle.stroke = colorScheme.dark;
  naElementStyle.fill = "#dddddd";
  heterocygousElementStyle.fill = colorScheme.lighter;
  noCallElementStyle.fill = "#bbbbbb";

  if (selected.length > 0) {
    rangeElementStyle.fill = selectedColorScheme.medium;
    symbolElementStyle.fill = selectedColorScheme.medium;
    symbolElementStyle.stroke = selectedColorScheme.dark;
    naElementStyle.stroke = selectedColorScheme.dark;
    heterocygousElementStyle.stroke = selectedColorScheme.dark;
    noCallElementStyle.stroke = selectedColorScheme.dark;
  }

  pileupVariants.forEach((v: Variant) => {
    profileElementStyles[v.id] = {};
    connectorElementStyles[v.id] = {};
    if (selected.find((varId: number) => varId === v.id)) {
      profileElementStyles[v.id].fill = selectedColorScheme.medium;
      profileElementStyles[v.id].stroke = selectedColorScheme.dark;
      connectorElementStyles[v.id].stroke = selectedColorScheme.dark;
    } else {
      profileElementStyles[v.id].fill = colorScheme.medium;
      profileElementStyles[v.id].stroke = colorScheme.dark;

      //connectorElementStyles[v.id].stroke = // default from CSS or Konva code
      //profileElementStyles[v.id].stroke = // default from CSS or Konva code
    }
  });

  return {
    symbolElementStyle,
    rangeElementStyle,
    connectorElementStyles,
    profileElementStyles,
    naElementStyle,
    heterocygousElementStyle,
    noCallElementStyle,
  };
};

export { createVariantShape };
